'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrdersWithoutWeightFilters } from '../../hooks/useOrdersWithoutWeightFilters';

interface OrdersWithoutWeightFiltersPanelProps {
  /** Condiciones iniciales desde query params */
  initialConditions?: FilterConditionOutput[];
  /** Callback cuando cambian los filtros */
  onChange?: (conditions: FilterConditionOutput[]) => void;
  /** Si mostrar el filtro de cliente */
  hasTeamData?: boolean;
  /** Clases CSS adicionales */
  className?: string;
}

/**
 * Componente OrdersWithoutWeightFiltersPanel
 *
 * Panel de filtros simplificados para el modal/dropdown de filtros.
 * Layout en grid de 2 columnas que se adapta a diferentes tamaños.
 */
export const OrdersWithoutWeightFiltersPanel = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
  className,
}: OrdersWithoutWeightFiltersPanelProps) => {
  const { t } = useTranslation('ordersWithoutWeightModule');

  const {
    filters,
    handleProcessedAtChange,
    handleLastShipmentUpdateChange,
    handleShipmentStatusChange,
    handleShopChange,
    handleCourierChange,
    handleClientChange,
    handleMigrationFilterChange,
    shipmentStatusOptions,
    shopOptions,
    courierOptions,
    clientOptions,
    isLoadingShops,
    isLoadingCouriers,
    isLoadingClients,
    clearAllFilters,
  } = useOrdersWithoutWeightFilters({
    initialConditions,
    onChange,
    hasTeamData,
  });

  // Contar filtros activos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.processedAt?.from && filters.processedAt?.to) count++;
    if (filters.lastShipmentUpdate?.from && filters.lastShipmentUpdate?.to) count++;
    if (filters.shipmentStatus.length > 0) count++;
    if (filters.shop.length > 0) count++;
    if (filters.courier.length > 0) count++;
    if (filters.client.length > 0) count++;
    if (filters.migrationFilter !== 'all') count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      {/* SECCIÓN: FECHAS */}
      <FilterSection title={t('filters.dates', 'Fechas')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.processedAt', 'Fecha de procesamiento')}>
            <DateRangePicker
              initialDateFrom={filters.processedAt?.from}
              initialDateTo={filters.processedAt?.to}
              onUpdate={({ range }) => handleProcessedAtChange(range)}
              placeholder="Seleccionar..."
              dataTestId="filter-processed-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.lastShipmentUpdate', 'Última actualización')}>
            <DateRangePicker
              initialDateFrom={filters.lastShipmentUpdate?.from}
              initialDateTo={filters.lastShipmentUpdate?.to}
              onUpdate={({ range }) => handleLastShipmentUpdateChange(range)}
              placeholder="Seleccionar..."
              dataTestId="filter-last-shipment-update"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      {/* SECCIÓN: OTROS FILTROS */}
      <FilterSection title={t('filters.other', 'Filtros')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.shop', 'Tienda')}>
            <MultiSelect
              options={shopOptions}
              value={filters.shop}
              onValueChange={handleShopChange}
              placeholder={isLoadingShops ? 'Cargando...' : 'Todas las tiendas'}
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingShops}
              dataTestId="filter-shop"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.shipmentStatus', 'Estado de envío')}>
            <MultiSelect
              options={shipmentStatusOptions}
              value={filters.shipmentStatus}
              onValueChange={handleShipmentStatusChange}
              placeholder="Todos los estados"
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-shipment-status"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.courier', 'Courier')}>
            <MultiSelect
              options={courierOptions}
              value={filters.courier}
              onValueChange={handleCourierChange}
              placeholder={isLoadingCouriers ? 'Cargando...' : 'Todos los couriers'}
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingCouriers}
              dataTestId="filter-courier"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          {hasTeamData && (
            <FilterField label={t('filters.client', 'Cliente')}>
              <MultiSelect
                options={clientOptions}
                value={filters.client}
                onValueChange={handleClientChange}
                placeholder={isLoadingClients ? 'Cargando...' : 'Todos los clientes'}
                emptyText={t('filters.notFound', 'Sin resultados')}
                disabled={isLoadingClients}
                dataTestId="filter-client"
                triggerClassName="h-[38px]"
              />
            </FilterField>
          )}

          <FilterField label={t('filters.migration', 'Pedido migrado')}>
            <Select
              value={filters.migrationFilter}
              onValueChange={(v) => handleMigrationFilterChange(v as 'all' | 'migrated' | 'not_migrated')}
            >
              <SelectTrigger className="h-[38px] text-sm" data-testid="filter-migration">
                <SelectValue placeholder={t('filters.migration_all', 'Todos')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.migration_all', 'Todos')}</SelectItem>
                <SelectItem value="migrated">{t('filters.migration_migrated', 'Solo migrados')}</SelectItem>
                <SelectItem value="not_migrated">{t('filters.migration_not_migrated', 'Solo no migrados')}</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </div>
      </FilterSection>

      {/* Footer con acciones */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Componente auxiliar para sección de filtros con título
 */
interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
}

const FilterSection = ({ title, children }: FilterSectionProps) => (
  <div className="space-y-3">
    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

/**
 * Componente auxiliar para cada campo de filtro con label
 */
interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
